/* everstory-meta.js — the browser half of Meta tracking.
 *
 * WHY THIS IS IN THE THEME AND NOT A CUSTOM PIXEL
 * A Shopify custom pixel runs in a sandboxed iframe on a foreign origin, so fbevents.js cannot set
 * _fbp on tryeverstory.com from there. That is the same wall that pushed GA4 onto the Measurement
 * Protocol (01-studio/analytics-custom-pixel.js). Here, in the real page, the cookie is first-party
 * and real. The checkout half of the funnel is not theme-editable at all and arrives server-side
 * instead, from 10-events/functions/webhooks/shopify.js.
 *
 * CONSENT
 * Nothing loads and nothing fires until Shopify's Customer Privacy API reports marketing consent.
 * Events raised before the visitor has answered are queued, then flushed on accept or dropped on
 * decline. That is the decision recorded for this store: the browser pixel is consent-gated, while
 * the server-side Purchase always sends as a record of a completed contract.
 *
 * window.esFbq(name, data, eventId) is the only entry point; everstory-reskin.js and its two
 * siblings call it from the track() helper they already had.
 */
(function () {
  var CFG = window.EverstoryMeta || {};
  var PIXEL = (CFG.pixelId || '').trim();
  if (!PIXEL) return;

  var queue = [];
  var loaded = false;
  var decided = false;
  var allowed = false;

  // ---- cookies ---------------------------------------------------------------------------------

  function cookie(name) {
    var m = document.cookie.match(new RegExp('(^|;\\s*)' + name + '=([^;]*)'));
    return m ? decodeURIComponent(m[2]) : '';
  }

  /**
   * The cookieless counter, which is deliberately NOT this file's business and is called from it
   * anyway. everstory-hit.js owns it and loads without consent; the two events below are the ones
   * only this file observes, and a second copy of the add-to-cart hook would be a second definition
   * of what an add to cart is. Guarded, so the pixel survives the counter being absent.
   */
  function hit(event) {
    try { if (typeof window.esHit === 'function') window.esHit(event); } catch (e) {}
  }

  /**
   * _fbc is normally set by fbevents.js when a visitor arrives on an ad click, but only if the
   * pixel is already loaded at that moment. Behind a consent banner it usually is not, so the
   * click id is reconstructed from the URL in Meta's documented format.
   */
  function clickId() {
    var existing = cookie('_fbc');
    if (existing) return existing;
    var fbclid = new URLSearchParams(location.search).get('fbclid');
    return fbclid ? 'fb.1.' + Date.now() + '.' + fbclid : '';
  }

  // ---- handing identity to the server ----------------------------------------------------------

  /**
   * Write _fbp / _fbc onto the cart so they arrive on the order as note_attributes, which is what
   * lets the server-side Purchase be matched to this browser session. The leading underscore is
   * Shopify's convention for an attribute the customer never sees, the same one everstory-upload.js
   * uses for photo URLs.
   *
   * Only ever written once per session, and only with a value: an empty write would clear a good
   * attribute captured on an earlier page.
   *
   * IT WAITS FOR THE COOKIE, AND THAT WAIT IS THE POINT.
   * fbevents.js writes _fbp a moment after it starts executing, while flush() calls this the instant
   * the script tag is inserted. The first version read the cookie once and gave up, so it only ever
   * succeeded from the second page load of a session onwards. A visitor who landed, added to cart
   * and checked out on one page handed the server nothing, and every such Purchase was matched on
   * a hashed email alone. Measured on the live product page: cart attributes {} on first load,
   * {"_fbp":"fb.1...."} after a reload.
   */
  var sharing = false;
  var sharedClick = false;

  function postAttributes(attributes, flag) {
    fetch(cartUrl('cart/update.js'), {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ attributes: attributes })
    }).then(function (r) {
      try { sessionStorage.setItem(flag, '1'); } catch (e) {}
      return r.json();
    }).then(function (cart) {
      // Always from the response, never from a cached token. Both writers land here, and a post on
      // an empty cart mints a new token, so the token this page believes in has to be the newest one
      // the server issued or the two halves of InitiateCheckout key on different carts.
      if (cart && cart.token) rememberCart(cart);
    }).catch(function () {});
  }

  /**
   * THE CLICK ID IS CAPTURED FOR EVERY VISITOR, CONSENTING OR NOT, AND THE PIXEL STILL IS NOT.
   *
   * `_fbc` is not a cookie read: clickId() rebuilds it from `fbclid` in the URL, and the cart
   * attribute it lands in is server-side state on a cart the visitor asked for. Nothing is stored on
   * the device that was not already, so this needs no marketing consent, while fbevents.js still
   * loads only on a yes.
   *
   * It matters because of what was measured on 2026-08-18: consent is required in 31 countries and
   * 7.3% of August's sessions produced any behavioural data at all. This write used to hang off
   * flush(), so an ad click that bought something without accepting cookies reached Meta as a
   * Purchase matched on a hashed email alone, with no way to tie it to the ad that paid for it.
   * Purchase optimisation cannot work on that. ADR-019.
   *
   * ONLY WITH AN fbclid IN THE URL. The file's other warning still holds: every /cart/update.js on
   * an empty cart mints a new cart token. Gating on the click id keeps this to the ad-click case,
   * which is the only case where there is anything to write.
   */
  function shareClickId() {
    try {
      if (sharedClick) return;
      if (!new URLSearchParams(location.search).get('fbclid')) return;
      if (sessionStorage.getItem('es_meta_fbc') === '1') return;
      var fbc = clickId();
      if (!fbc) return;
      sharedClick = true;
      postAttributes({ _fbc: fbc }, 'es_meta_fbc');
    } catch (e) {}
  }

  function shareWithServer(attempt) {
    try {
      // ONE CHAIN PER PAGE LOAD. decide() can run twice: once from the consent event Shopify
      // publishes and once from the poll that reads the same answer a moment later. Both called
      // this, both found no _fbp yet, and both started their own retry chain, so /cart/update.js
      // was posted twice. On an empty cart every post mints a NEW cart token, and that token is now
      // the InitiateCheckout deduplication key, so the churn is not cosmetic.
      if (!attempt && sharing) return;
      if (!attempt) sharing = true;
      if (sessionStorage.getItem('es_meta_cart') === '1') return;
      var fbp = cookie('_fbp');
      var fbc = clickId();
      // THE RETRY WAITS FOR _fbp, NOT FOR "EITHER OF THEM", and that distinction was a live bug for
      // every ad click. The bar used to be `!fbp && !fbc`, and clickId() reads fbclid straight out of
      // the URL, so on an ad click fbc existed on the first attempt, the retry chain was skipped, the
      // cart was written with _fbc alone and es_meta_cart marked the job done. The cookie fbevents.js
      // writes a moment later never reached the server, on exactly the traffic that was paid for.
      // Found by scripts/pixel-check.mjs once it started driving an ad-style URL. S128.
      if (!fbp && (attempt || 0) < 20) {
        setTimeout(function () { shareWithServer((attempt || 0) + 1); }, 500);
        return;
      }
      // Nothing new to say: the click id is already on the cart and there is no cookie to add.
      if (!fbp && (sharedClick || !fbc)) return;
      var attributes = {};
      if (fbp) attributes._fbp = fbp;
      if (fbc) attributes._fbc = fbc;
      // Its own flag, so the unconsented click-id write cannot make this one look done. _fbp only
      // ever exists after fbevents.js has run, which only ever happens on a yes, so a session that
      // accepts late still has to come through here to hand the cookie over.
      postAttributes(attributes, 'es_meta_cart');
    } catch (e) {}
  }

  function cartUrl(path) {
    return window.Shopify && Shopify.routes && Shopify.routes.root
      ? Shopify.routes.root + path : '/' + path;
  }

  // ---- the cart, as the checkout event needs to describe it -------------------------------------

  /**
   * The cart token is the deduplication key.
   *
   * 10-events/functions/webhooks/shopify.js builds its InitiateCheckout event_id from
   * `cart_token` on the checkouts/create payload. Using the same token here means Meta treats the
   * two as one event, so the server copy can keep sending whenever it has an identity to send
   * without the risk of counting a checkout twice.
   */
  var cartState = null;

  function rememberCart(cart) {
    if (!cart || !cart.token) return;
    cartState = {
      token: cart.token,
      value: Number(cart.total_price || 0) / 100,
      currency: cart.currency || (CFG.viewContent && CFG.viewContent.currency) || 'EUR',
      count: Number(cart.item_count || 0),
      ids: (cart.items || []).map(function (i) { return String(i.product_id || i.id); })
    };
  }

  function refreshCart() {
    return fetch(cartUrl('cart.js'), { headers: { Accept: 'application/json' } })
      .then(function (r) { return r.json(); })
      .then(function (cart) { rememberCart(cart); return cartState; })
      .catch(function () { return cartState; });
  }

  // ---- the pixel itself -------------------------------------------------------------------------

  function loadPixel() {
    if (loaded) return;
    loaded = true;
    /* eslint-disable */
    !function (f, b, e, v, n, t, s) {
      if (f.fbq) return; n = f.fbq = function () {
        n.callMethod ? n.callMethod.apply(n, arguments) : n.queue.push(arguments);
      };
      if (!f._fbq) f._fbq = n; n.push = n; n.loaded = !0; n.version = '2.0'; n.queue = [];
      t = b.createElement(e); t.async = !0; t.src = v;
      s = b.getElementsByTagName(e)[0]; s.parentNode.insertBefore(t, s);
    }(window, document, 'script', 'https://connect.facebook.net/en_US/fbevents.js');
    /* eslint-enable */

    var init = {};
    var fbc = clickId();
    if (fbc) init.fbc = fbc;
    window.fbq('init', PIXEL, init);
  }

  function flush() {
    loadPixel();
    var pending = queue.splice(0, queue.length);
    for (var i = 0; i < pending.length; i++) send.apply(null, pending[i]);
    shareWithServer();
    // A visitor arriving on a page with a cart already in it (the cart page, a second visit) needs
    // the token before the checkout button is pressed. One request, and only when there is a cart.
    if (CFG.cartCount > 0) refreshCart();
  }

  function send(name, data, eventId) {
    var custom = name.charAt(0) === '_';
    var method = custom ? 'trackCustom' : 'track';
    var label = custom ? name.slice(1) : name;
    try {
      window.fbq(method, label, data || {}, eventId ? { eventID: eventId } : undefined);
    } catch (e) {}
  }

  /** A stable-ish id per event, so a server event for the same action can be deduplicated. */
  function newId(name) {
    return name + '.' + Date.now() + '.' + Math.random().toString(36).slice(2, 8);
  }

  window.esFbq = function (name, data, eventId) {
    if (!decided) { queue.push([name, data, eventId || newId(name)]); return; }
    if (!allowed) return;
    send(name, data, eventId || newId(name));
  };

  // ---- add to cart --------------------------------------------------------------------------------

  /**
   * Caught at the form rather than inside Dawn's <product-form>, so this survives a Dawn upgrade.
   * Capture phase, because Dawn calls preventDefault and submits over fetch.
   */
  function watchAddToCart() {
    document.addEventListener('submit', function (event) {
      var form = event.target;
      if (!form || !form.action || form.action.indexOf('/cart/add') === -1) return;
      var radio = form.querySelector('[data-variant-radio]:checked');
      var id = radio ? radio.value : (form.querySelector('[name="id"]') || {}).value;
      var amount = radio ? parseFloat(radio.dataset.amount) : NaN;
      // The counter first, because it is the half that reports on a declined banner. One hook for
      // both, so the pixel and the counter can never disagree about what an add to cart is.
      hit('atc_click');
      window.esFbq('AddToCart', {
        content_type: 'product',
        content_ids: CFG.viewContent ? CFG.viewContent.content_ids : [],
        contents: id ? [{ id: String(id), quantity: 1 }] : [],
        value: isNaN(amount) ? 0 : amount,
        currency: (CFG.viewContent && CFG.viewContent.currency) || 'EUR'
      });
      // Dawn posts the form over fetch, so the cart has a token a moment from now. Read it while
      // there is time: the checkout click that follows cannot wait for a round trip.
      setTimeout(refreshCart, 1200);
    }, true);
  }

  // ---- checkout ---------------------------------------------------------------------------------

  /**
   * InitiateCheckout, fired from the page rather than from the webhook.
   *
   * The server-side copy was rejected by Meta on the only real checkout it ever saw: code 100,
   * subcode 2804050, no sufficient customer information. `checkouts/create` arrives before the
   * customer has typed an email, so with no _fbp on the cart there was nothing to match on. Here
   * the cookie exists, and the click is the moment the visitor actually starts checking out.
   *
   * Capture phase and no waiting: the browser is about to navigate, and fbevents sends its beacon
   * synchronously, so the event has to be raised before anything else is allowed to happen.
   */
  function fireInitiateCheckout() {
    var c = cartState;
    // Counted before the cart-state guard, because a declined visitor pressing checkout is exactly
    // what this counter exists to see and cartState can legitimately be empty here. Its own
    // once-per-session flag: the guard below is keyed on the cart token, which is the wrong key for
    // a count, and a double-tapped button is one visitor reaching checkout, not two.
    try {
      if (sessionStorage.getItem('es_hit_checkout') !== '1') {
        sessionStorage.setItem('es_hit_checkout', '1');
        hit('checkout_click');
      }
    } catch (e) { hit('checkout_click'); }
    if (!c || !c.token || sessionStorage.getItem('es_meta_ic') === c.token) return;
    try { sessionStorage.setItem('es_meta_ic', c.token); } catch (e) {}
    window.esFbq('InitiateCheckout', {
      content_type: 'product',
      content_ids: c.ids,
      value: c.value,
      currency: c.currency,
      num_items: c.count
    }, 'initiatecheckout:' + c.token);
  }

  function watchCheckout() {
    document.addEventListener('click', function (event) {
      var el = event.target && event.target.closest
        ? event.target.closest('[name="checkout"], a[href*="/checkout"]')
        : null;
      if (el) fireInitiateCheckout();
    }, true);

    // The cart form can also be submitted with the checkout button as its submitter, in which case
    // no click ever reaches the button itself.
    document.addEventListener('submit', function (event) {
      var form = event.target;
      if (!form || !form.action || form.action.indexOf('/cart') === -1) return;
      if (event.submitter && event.submitter.name === 'checkout') fireInitiateCheckout();
    }, true);
  }

  // ---- consent ----------------------------------------------------------------------------------

  function decide(marketing) {
    decided = true;
    allowed = marketing === true;
    if (allowed) flush();
    else queue.length = 0;
  }

  /**
   * A RECORDED ANSWER AND A PERMISSION ARE NOT THE SAME QUESTION, AND READING ONLY THE FIRST KEPT THE
   * PIXEL DARK FOR EVERY VISITOR THE ADS BUY.
   *
   * `currentVisitorConsent().marketing` is what the visitor was recorded as SAYING. Consent is
   * required in 31 countries and in none of the other 288, so outside them nobody is ever asked,
   * nothing is ever recorded, and it stays '' for the life of the session. This function used to read
   * that alone and return null, so decide() never ran, flush() never ran, and fbevents.js never
   * loaded. The 20-poll backstop below re-read the same '' twenty times and gave up.
   *
   * `marketingAllowed()` is whether marketing may actually FIRE, which is exactly true in that case
   * and false in a regulated region that has not answered yet. So it is the right fallback and only
   * the fallback: an explicit yes or no still wins, and a pending answer still returns null so the
   * banner path is untouched.
   *
   * Measured 2026-08-18 against the live product page with Shopify's API stubbed to the shape an
   * unregulated visitor gets: identical loads, marketing '' produced no fbevents.js and marketing
   * 'yes' produced fbevents.js, signals/config and a /tr beacon. The ad set is US-only, so this was
   * 100% of paid traffic, and Meta reported 0 landing page views against 38 link clicks.
   */
  function readConsent() {
    try {
      var api = window.Shopify && window.Shopify.customerPrivacy;
      if (!api || !api.currentVisitorConsent) return null;
      var c = api.currentVisitorConsent();
      if (c && c.marketing === 'yes') return true;
      if (c && c.marketing === 'no') return false;
      // Nothing recorded. Allowed here means no answer is owed and none is coming.
      if (typeof api.marketingAllowed === 'function' && api.marketingAllowed() === true) return true;
      return null; // an answer is owed and has not arrived
    } catch (e) {
      return null;
    }
  }

  /**
   * The Customer Privacy API is NOT present by default on this store; verified in Chrome on the
   * live product page, where window.Shopify.customerPrivacy stayed undefined indefinitely. It has
   * to be asked for. Without this call the pixel waits for an answer that never arrives and stays
   * dark forever, which is a silent failure rather than a visible one.
   */
  function loadConsentApi(done) {
    try {
      if (window.Shopify && typeof window.Shopify.loadFeatures === 'function') {
        window.Shopify.loadFeatures([{ name: 'consent-tracking-api', version: '0.1' }], function () {
          done();
        });
        return;
      }
    } catch (e) {}
    done();
  }

  function start() {
    // Before the consent dance, because it is not part of it. See shareClickId().
    shareClickId();

    var now = readConsent();
    if (now !== null) decide(now);

    // A later accept has to start the session cleanly, so the answer is watched either way.
    document.addEventListener('visitorConsentCollected', function (event) {
      var marketing = event && event.detail
        ? event.detail.marketingAllowed === true || event.detail.marketing_allowed === true
        : false;
      decided = false;
      decide(marketing);
    });

    // Ask for the API, then read it. The poll stays as a backstop for the case where the feature
    // arrives a moment after its own callback fires.
    if (now === null) {
      loadConsentApi(function () {
        var afterLoad = readConsent();
        if (afterLoad !== null) { decide(afterLoad); return; }
        var tries = 0;
        var poll = setInterval(function () {
          var later = readConsent();
          if (later !== null || ++tries > 20) {
            clearInterval(poll);
            if (later !== null) decide(later);
          }
        }, 500);
      });
    }

    // PageView, plus whatever the page declared about itself in the snippet.
    window.esFbq('PageView');
    if (CFG.viewContent) window.esFbq('ViewContent', CFG.viewContent);
    watchAddToCart();
    watchCheckout();
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', start);
  else start();
})();
